import { zodResolver } from '@hookform/resolvers/zod';
import { Allocations } from 'components/AllocationsTable/types';
import { useLayoutContext } from 'contexts/LayoutContext';
import { ActionStatus } from 'further-types/lp/action';
import { CapitalActionEntryType } from 'further-types/lp/capital-action';
import { CommitmentType } from 'further-types/lp/vehicle';
import { capitalAction as rules } from 'further-ui/validations';
import { handleTrpcError } from 'hooks/ui/useHandleTrpcMutation';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import useDefaultEntryValues from './useDefaultEntryValues';

const formSchema = (id?: string) =>
  id ? rules.UpdateCapitalAction : rules.CreateCapitalAction;

export enum Steps {
  Details,
  Transactions,
  Review,
}

type CapitalAction = {
  id: string;
  status: ActionStatus;
};

export type Action = 'saveAndExit' | 'saveAndNext';

export type CapitalActionFormState = {
  allocations: Record<string, Array<Allocations>>;
};

export type CreateCapitalActionFormValues = z.infer<
  typeof rules.CreateCapitalAction
>;
export type UpdateCapitalActionFormValues = z.infer<
  typeof rules.UpdateCapitalAction
>;
export type CapitalActionFormValues =
  | CreateCapitalActionFormValues
  | UpdateCapitalActionFormValues;

function getStepValidationFields(step: Steps) {
  switch (step) {
    case Steps.Details:
      return ['vehicleId', 'name', 'ledgerDate', 'actionDueDate'] as const;
    case Steps.Transactions:
      return ['entries'] as const;
  }

  return [];
}

function isUpdateValues(
  values: CapitalActionFormValues,
): values is UpdateCapitalActionFormValues {
  return 'id' in values;
}

export default function useCapitalActionForm({
  id,
  vehicleId,
  defaultValues,
  hasUncalledEqualisations,
  hasUncalledPartnerTransfers,
}: {
  id?: string;
  vehicleId: string;
  defaultValues?: UpdateCapitalActionFormValues;
  hasUncalledEqualisations: boolean;
  hasUncalledPartnerTransfers: boolean;
}) {
  const apiUtils = api.useUtils();
  const { error } = useNotification();
  const [lastAction, setLastAction] = useState<Action | null>(null);
  const { vehicle } = useLayoutContext();
  const isCapitalAndLoan =
    vehicle?.commitmentType === CommitmentType.CapitalAndLoan;
  const hasUncalledCapitalContribution =
    vehicle?.hasUncalledCapitalContribution ?? false;
  const { defaultEntry } = useDefaultEntryValues({
    capitalContributionIsDefault:
      isCapitalAndLoan && hasUncalledCapitalContribution,
    equalisationIsDefault: hasUncalledEqualisations,
    partnerTransferIsDefault: hasUncalledPartnerTransfers,
  });

  const formMethods = useForm<CapitalActionFormValues>({
    shouldFocusError: true,
    resolver: zodResolver(formSchema(id)),
    defaultValues: defaultValues
      ? {
          ...defaultValues,
          vehicleId,
          entries: defaultValues.entries?.length
            ? defaultValues.entries
            : [defaultEntry],
        }
      : {
          ...(id ? { id } : {}),
          vehicleId,
          name: '',
          status: ActionStatus.Draft,
          ledgerDate: undefined,
          actionDueDate: undefined,
          entries: [defaultEntry],
          transactions: [],
        },
    criteriaMode: 'all',
    resetOptions: {
      keepDefaultValues: false,
    },
  });

  const createCapitalAction = api.capitalAction.create.useMutation();
  const updateCapitalAction = api.capitalAction.update.useMutation();

  const getUpdateValues = (
    values: UpdateCapitalActionFormValues,
    step: Steps,
  ) => {
    switch (step) {
      case Steps.Details:
        return {
          id: values.id,
          vehicleId: values.vehicleId,
          name: values.name,
          ledgerDate: values.ledgerDate,
          actionDueDate: values.actionDueDate,
          status: values.status,
        };
      default:
        return values;
    }
  };

  const filterOutEmptyExpenseOrInvestmentEntries = (
    entries: UpdateCapitalActionFormValues['entries'],
  ) => {
    const expenseOrInvestmentTypes = [
      CapitalActionEntryType.Expense,
      CapitalActionEntryType.Investment,
    ];

    if (!entries?.length) return entries;

    const filteredEntries = entries.filter((entry) =>
      expenseOrInvestmentTypes.includes(entry.entryType)
        ? entry.actions.length > 0
        : true,
    );

    return filteredEntries.length > 0 ? filteredEntries : undefined;
  };

  const handleSubmit = (
    values: CapitalActionFormValues,
    step: Steps,
    onSuccess: (capitalAction: CapitalAction) => void,
  ) => {
    const mutateOptions = {
      onSuccess: async (capitalAction: CapitalAction) => {
        onSuccess(capitalAction);
        await Promise.all([
          apiUtils.capitalAction.get.invalidate(),
          apiUtils.capitalAction.getVehicleCapitalActionsSummary.invalidate(),
        ]);
      },
      onError: handleTrpcError(error),
    };

    if (isUpdateValues(values)) {
      const updateValues = getUpdateValues(values, step);
      updateCapitalAction.mutate(updateValues, mutateOptions);
    } else {
      createCapitalAction.mutate(values, mutateOptions);
    }
  };

  const submitStep = async (
    step: Steps,
    onSuccess: (capitalAction: CapitalAction) => void,
    options?: { approve?: boolean; saveAndExit?: boolean },
  ) => {
    const shouldApprove = options?.approve ?? step === Steps.Review;
    const values = formMethods.getValues();

    if (shouldApprove) {
      formMethods.handleSubmit((values) =>
        handleSubmit(
          { ...values, status: ActionStatus.Approved },
          Steps.Review,
          onSuccess,
        ),
      )();
    } else if (
      step === Steps.Transactions &&
      options?.saveAndExit &&
      isUpdateValues(values)
    ) {
      const entries = filterOutEmptyExpenseOrInvestmentEntries(values.entries);
      handleSubmit({ ...values, entries }, step, onSuccess);
    } else if (await formMethods.trigger(getStepValidationFields(step))) {
      handleSubmit(values, step, onSuccess);
    }
  };

  return {
    formMethods,
    submitStep,
    isLoading: createCapitalAction.isPending || updateCapitalAction.isPending,
    lastAction,
    setLastAction,
  };
}
